import apiClient from '@/lib/api'

// ─── Typen (Backend-Spiegel) ────────────────────────────────────────────────

export type SyncStatus =
  | 'pending'
  | 'synced'
  | 'failed'
  | 'ignored'
  | 'retrying'
  | 'needs_manual_action'

export type MappingStatus =
  | 'active'
  | 'not_found'
  | 'ambiguous'
  | 'disabled'
  | 'needs_manual_action'

export interface BridgeMetrics {
  activeTicketSyncs: number
  failedTicketSyncs: number
  pendingMessageSyncs: number
  failedMessageSyncs: number
  expiredSubscriptions: number
  lastPollAt: string | null
}

export type ChatStatus = 'open' | 'closed'

export interface TicketSync {
  id: string
  tanss_ticket_id: string | null
  tanss_chat_id: string
  teams_chat_id: string | null
  teams_topic: string | null
  ticket_url: string | null
  pinned_message_id: string | null
  status: SyncStatus
  /** Lifecycle des TANSS-Chats. `closed` → Spiegelung pausiert. */
  chat_status?: ChatStatus
  last_status_change_at?: string | null
  last_sync_at: string | null
  last_error: string | null
  created_at: string
  updated_at: string
}

export interface MessageSync {
  id: string
  ticketSyncId: string
  origin: 'TANSS' | 'TEAMS' | 'SYSTEM'
  direction: 'TANSS_TO_TEAMS' | 'TEAMS_TO_TANSS'
  tanssChatId: string | null
  tanssMessageId: string | null
  teamsChatId: string | null
  teamsMessageId: string | null
  contentHash: string | null
  status: SyncStatus
  lastError: string | null
}

export interface UserMapping {
  id: string
  tanssUserId: string
  tanssDisplayName: string | null
  tanssShortName: string | null
  teamsEmail: string | null
  teamsUserId: string | null
  teamsDisplayName: string | null
  status: MappingStatus
  lastVerifiedAt: string | null
  lastError: string | null
}

export interface BridgeSubscription {
  id: string
  ticket_sync_id: string
  subscription_id: string
  teams_chat_id: string
  resource: string
  expires_at: string
  status: 'active' | 'renewing' | 'expired' | 'failed'
  last_renewal_at: string | null
  last_error: string | null
  tanss_chat_id?: string
  tanss_ticket_id?: string | null
}

export interface AuditEntry {
  id: string
  action: string
  direction: 'tanss_to_teams' | 'teams_to_tanss' | 'no_op'
  correlation_id: string | null
  before_json: unknown
  after_json: unknown
  error: string | null
  note: string | null
  created_at: string
}

export interface TicketSyncDetail {
  ticketSync: TicketSync
  messages: MessageSync[]
  audit: AuditEntry[]
}

// ─── API ─────────────────────────────────────────────────────────────────────

const BASE = '/tanss-teams-bridge'

export async function getMetrics(): Promise<BridgeMetrics> {
  const { data } = await apiClient.get<{ metrics: BridgeMetrics }>(`${BASE}/metrics`)
  return data.metrics
}

export async function listTicketSyncs(opts: {
  status?: SyncStatus
  search?: string
  limit?: number
  offset?: number
}): Promise<TicketSync[]> {
  const { data } = await apiClient.get<{ items: TicketSync[] }>(`${BASE}/ticket-syncs`, {
    params: opts,
  })
  return data.items
}

export async function getTicketSync(id: string): Promise<TicketSyncDetail> {
  const { data } = await apiClient.get<TicketSyncDetail>(`${BASE}/ticket-syncs/${id}`)
  return data
}

export async function retryTicketSync(id: string): Promise<void> {
  await apiClient.post(`${BASE}/ticket-syncs/${id}/retry`, {})
}

export async function listMessageSyncs(opts: {
  ticketSyncId?: string
  status?: SyncStatus
  limit?: number
  offset?: number
}): Promise<MessageSync[]> {
  const { data } = await apiClient.get<{ items: MessageSync[] }>(`${BASE}/message-syncs`, {
    params: opts,
  })
  return data.items
}

export async function retryMessageSync(id: string): Promise<void> {
  await apiClient.post(`${BASE}/message-syncs/${id}/retry`, {})
}

export async function listUserMappings(opts: {
  limit?: number
  offset?: number
} = {}): Promise<UserMapping[]> {
  const { data } = await apiClient.get<{ items: UserMapping[] }>(`${BASE}/user-mappings`, {
    params: opts,
  })
  return data.items
}

export async function refreshUserMapping(tanssUserId: string): Promise<UserMapping> {
  const { data } = await apiClient.post<{ mapping: UserMapping }>(
    `${BASE}/user-mappings/${encodeURIComponent(tanssUserId)}/refresh`,
    {},
  )
  return data.mapping
}

export async function listSubscriptions(): Promise<BridgeSubscription[]> {
  const { data } = await apiClient.get<{ items: BridgeSubscription[] }>(`${BASE}/subscriptions`)
  return data.items
}

export interface StartMessageVariable {
  key: string
  label: string
  description: string
}

export interface StartMessageConfig {
  template: string
  default: string
  variables: StartMessageVariable[]
}

export async function getStartMessage(): Promise<StartMessageConfig> {
  const { data } = await apiClient.get<StartMessageConfig>(`${BASE}/start-message`)
  return data
}

export async function saveStartMessage(template: string): Promise<{ template: string }> {
  const { data } = await apiClient.put<{ template: string }>(`${BASE}/start-message`, { template })
  return data
}

export async function previewStartMessage(template: string): Promise<string> {
  const { data } = await apiClient.post<{ html: string }>(
    `${BASE}/start-message/preview`,
    { template },
  )
  return data.html
}

export interface DeptMemberDebug {
  tanssId: number
  name: string
  tanssEmail: string | null
  emailError: string | null
  sysmanUser: { id: string; name: string; email: string; entraId: string | null } | null
  mapping: { entraId: string | null; email: string | null; status: string; lastError: string | null } | null
  canSync: boolean
}

export interface DeptDebugResult {
  departmentId: number
  deptError: string | null
  memberCount: number
  members: DeptMemberDebug[]
}

export async function debugDept(departmentId: number): Promise<DeptDebugResult> {
  const { data } = await apiClient.get<DeptDebugResult>(`${BASE}/debug-dept`, {
    params: { departmentId },
  })
  return data
}

export interface DebugUser {
  id: string
  name: string
  email: string
  tanssId: string | null
  entraId: string | null
  isActive: boolean
  hasM365Token: boolean
  tanssDisplayName: string | null
  tanssShortName: string | null
  mappingTeamsEmail: string | null
  mappingStatus: string | null
  mappingLastError: string | null
}

export async function listDebugUsers(): Promise<DebugUser[]> {
  const { data } = await apiClient.get<{ items: DebugUser[] }>(`${BASE}/debug-users`)
  return data.items
}

export interface DiagnoseChatResult {
  tanssChatId: string
  ticketSync: Record<string, unknown> | null
  systemMessages: Array<{
    id: string
    teamsMessageId: string | null
    authorEntraId: string | null
    authorSysmanUserId: string | null
    authorName: string | null
    createdAt: string | null
  }>
  auditCloses: Array<{
    id: string
    action: string
    direction: string
    note: string | null
    error: string | null
    createdAt: string | null
  }>
  tanssLive: {
    reachable: boolean
    chatId: number | null
    createdByEmployeeId: number | null
    createdByName: string | null
    closedByEmployeeId: number | null
    closedByName: string | null
    ticketId: number | null
    error: string | null
  }
}

export async function diagnoseChat(tanssChatId: string): Promise<DiagnoseChatResult> {
  const { data } = await apiClient.get<DiagnoseChatResult>(`${BASE}/diagnose-chat`, {
    params: { tanssChatId },
  })
  return data
}
